import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import type { HandleCallbackActionParams } from '@domain-group/javascript-utils'

import type { PageContextPayload } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import CreateAgencyMarketingCampaignModal from '@modals/CreateAgencyMarketingCampaignModal'
import CreateAgencyMarketingOrderModal from '@modals/CreateAgencyMarketingOrderModal'

import handleCallbackAction from '@functions/handleCallbackAction'

import type { CampaignParams } from '@hooks/useAgencyMarketing'
import type { TemplatePageModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateAgencyMarketingCampaignModal: false,
  showCreateAgencyMarketingOrderModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = <ModalName extends keyof typeof componentCallbacks>(component: ModalName, setState: SetState) => {
  const componentCallbacks = {
    CreateAgencyMarketingCampaignModal: {
      closeModal: () => setState({ showCreateAgencyMarketingCampaignModal: false }),
      // @ts-expect-error TS2345
      createArtworkWithCampaign: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateAgencyMarketingOrderModal: {
      closeModal: () => setState({ showCreateAgencyMarketingOrderModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const Templates = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateAgencyMarketingCampaignModal, showCreateAgencyMarketingOrderModal } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateAgencyMarketingCampaignModal: (payload: {
        agencyMarketingCampaignParams: CampaignParams,
      }) => {
        setModalData('CreateAgencyMarketingCampaignModal', payload)
        setState({ showCreateAgencyMarketingCampaignModal: true })
      },
      showCreateAgencyMarketingOrderModal: (payload: { templatePage: TemplatePageModel }) => {
        setModalData('CreateAgencyMarketingOrderModal', payload)
        setState({ showCreateAgencyMarketingOrderModal: true })
      },
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateAgencyMarketingCampaignModal
          // @ts-expect-error TS2739
          callbacks={callbacks('CreateAgencyMarketingCampaignModal', setState)}
          showModal={showCreateAgencyMarketingCampaignModal}
        />

        <CreateAgencyMarketingOrderModal
          callbacks={callbacks('CreateAgencyMarketingOrderModal', setState)}
          showModal={showCreateAgencyMarketingOrderModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Templates
